// ============================================================================
// DRAWING TRANSMITTAL - Generate transmittal documents for drawing distribution
// Date: January 2026
// ============================================================================

'use client';

import React, { useState, useRef } from 'react';
import {
  X, FileText, Send, Download, Printer, Calendar, User,
  Building2, Mail, Phone, MapPin, Hash, Clock, CheckCircle,
  AlertCircle, Plus, Trash2, Edit2, Eye, Copy
} from 'lucide-react';
import type { Drawing, DrawingTransmittal, TransmittalItem } from './types';
import { DRAWING_DISCIPLINES, DRAWING_TYPES, STATUS_CONFIG } from './types';

// ============================================================================
// TYPES
// ============================================================================

interface DrawingTransmittalProps {
  drawings: Drawing[];
  isOpen: boolean;
  onClose: () => void;
  onSend: (transmittal: Omit<DrawingTransmittal, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  projectName?: string;
  projectNumber?: string;
  companyName?: string;
  companyLogo?: string;
}

interface TransmittalFormData {
  transmittal_number: string;
  project_name: string;
  project_number: string;
  date: string;
  from_company: string;
  from_name: string;
  from_email: string;
  from_phone: string;
  to_company: string;
  to_name: string;
  to_email: string;
  to_address: string;
  subject: string;
  reason: 'for_approval' | 'for_review' | 'for_information' | 'for_construction' | 'as_requested' | 'resubmission';
  notes: string;
  copies_to: string[];
  items: TransmittalItem[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TRANSMITTAL_REASONS = [
  { value: 'for_approval', label: 'For Approval', color: 'text-amber-400' },
  { value: 'for_review', label: 'For Review', color: 'text-blue-400' },
  { value: 'for_information', label: 'For Information', color: 'text-gray-400' },
  { value: 'for_construction', label: 'For Construction', color: 'text-green-400' },
  { value: 'as_requested', label: 'As Requested', color: 'text-purple-400' },
  { value: 'resubmission', label: 'Resubmission', color: 'text-cyan-400' }
];

const generateTransmittalNumber = (): string => {
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `TRA-${year}${month}-${random}`;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// ============================================================================
// PREVIEW COMPONENT
// ============================================================================

interface TransmittalPreviewProps {
  data: TransmittalFormData;
  companyLogo?: string;
}

function TransmittalPreview({ data, companyLogo }: TransmittalPreviewProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-white text-gray-900 rounded-lg overflow-hidden">
      {/* Preview content */}
      <div ref={printRef} className="p-8 print:p-4" id="transmittal-preview">
        {/* Header */}
        <div className="flex justify-between items-start border-b-2 border-gray-300 pb-4 mb-6">
          <div>
            {companyLogo ? (
              <img src={companyLogo} alt="Company Logo" className="h-12 mb-2" />
            ) : (
              <div className="text-2xl font-bold text-gray-800">{data.from_company || 'Company Name'}</div>
            )}
            <div className="text-sm text-gray-600 mt-1">
              <div>{data.from_name}</div>
              <div>{data.from_email}</div>
              {data.from_phone && <div>{data.from_phone}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-800 mb-2">TRANSMITTAL</div>
            <div className="text-sm text-gray-600">
              <div><span className="font-semibold">No:</span> {data.transmittal_number}</div>
              <div><span className="font-semibold">Date:</span> {formatDate(data.date)}</div>
            </div>
          </div>
        </div>

        {/* Project Info */}
        {(data.project_name || data.project_number) && (
          <div className="bg-gray-100 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              {data.project_name && (
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Project Name</span>
                  <div className="font-semibold">{data.project_name}</div>
                </div>
              )}
              {data.project_number && (
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Project Number</span>
                  <div className="font-semibold">{data.project_number}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* To/From */}
        <div className="grid grid-cols-2 gap-8 mb-6">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">To</div>
            <div className="border border-gray-300 rounded-lg p-3">
              <div className="font-semibold">{data.to_company || '-'}</div>
              <div className="text-sm text-gray-600">{data.to_name || '-'}</div>
              <div className="text-sm text-gray-600">{data.to_email || '-'}</div>
              {data.to_address && (
                <div className="text-sm text-gray-600 mt-2 whitespace-pre-line">{data.to_address}</div>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">From</div>
            <div className="border border-gray-300 rounded-lg p-3">
              <div className="font-semibold">{data.from_company || '-'}</div>
              <div className="text-sm text-gray-600">{data.from_name || '-'}</div>
              <div className="text-sm text-gray-600">{data.from_email || '-'}</div>
            </div>
          </div>
        </div>

        {/* Subject & Reason */}
        <div className="grid grid-cols-2 gap-8 mb-6">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Subject</div>
            <div className="font-semibold">{data.subject || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Transmitted For</div>
            <div className="font-semibold">
              {TRANSMITTAL_REASONS.find(r => r.value === data.reason)?.label || '-'}
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-6">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Documents Transmitted</div>
          <table className="w-full border-collapse border border-gray-300 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-3 py-2 text-left">#</th>
                <th className="border border-gray-300 px-3 py-2 text-left">Drawing Number</th>
                <th className="border border-gray-300 px-3 py-2 text-left">Title</th>
                <th className="border border-gray-300 px-3 py-2 text-center">Rev</th>
                <th className="border border-gray-300 px-3 py-2 text-center">Copies</th>
                <th className="border border-gray-300 px-3 py-2 text-left">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, idx) => (
                <tr key={idx}>
                  <td className="border border-gray-300 px-3 py-2">{idx + 1}</td>
                  <td className="border border-gray-300 px-3 py-2 font-mono">{item.drawing_number || '-'}</td>
                  <td className="border border-gray-300 px-3 py-2">{item.title}</td>
                  <td className="border border-gray-300 px-3 py-2 text-center">{item.revision}</td>
                  <td className="border border-gray-300 px-3 py-2 text-center">{item.copies}</td>
                  <td className="border border-gray-300 px-3 py-2 text-gray-600">{item.remarks || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Notes */}
        {data.notes && (
          <div className="mb-6">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Notes</div>
            <div className="border border-gray-300 rounded-lg p-3 text-sm whitespace-pre-line">
              {data.notes}
            </div>
          </div>
        )}

        {/* CC */}
        {data.copies_to.length > 0 && (
          <div className="mb-6">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Copies To</div>
            <div className="text-sm">{data.copies_to.join(', ')}</div>
          </div>
        )}

        {/* Signature */}
        <div className="grid grid-cols-2 gap-8 mt-8 pt-8 border-t border-gray-300">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-8">Issued By</div>
            <div className="border-b border-gray-400 w-48 mb-2"></div>
            <div className="text-sm">{data.from_name}</div>
            <div className="text-xs text-gray-500">{formatDate(data.date)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-8">Received By</div>
            <div className="border-b border-gray-400 w-48 mb-2"></div>
            <div className="text-sm text-gray-400">Signature / Date</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DrawingTransmittalModal({
  drawings,
  isOpen,
  onClose,
  onSend,
  projectName = '',
  projectNumber = '',
  companyName = '',
  companyLogo
}: DrawingTransmittalProps) {
  const [activeTab, setActiveTab] = useState<'form' | 'preview'>('form');
  const [isSending, setIsSending] = useState(false);
  const [ccInput, setCcInput] = useState('');
  
  const [formData, setFormData] = useState<TransmittalFormData>({
    transmittal_number: generateTransmittalNumber(),
    project_name: projectName,
    project_number: projectNumber,
    date: new Date().toISOString().split('T')[0],
    from_company: companyName,
    from_name: '',
    from_email: '',
    from_phone: '',
    to_company: '',
    to_name: '',
    to_email: '',
    to_address: '',
    subject: `Drawing Transmittal - ${projectName || 'Project'}`,
    reason: 'for_review',
    notes: '',
    copies_to: [],
    items: drawings.map(d => ({
      drawing_id: d.id,
      drawing_number: d.drawing_number || '',
      title: d.drawing_name,
      revision: d.revision,
      copies: 1,
      format: 'digital',
      remarks: ''
    }))
  });

  const updateField = <K extends keyof TransmittalFormData>(
    field: K,
    value: TransmittalFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateItem = (index: number, field: keyof TransmittalItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const addCC = () => {
    if (ccInput.trim() && !formData.copies_to.includes(ccInput.trim())) {
      updateField('copies_to', [...formData.copies_to, ccInput.trim()]);
      setCcInput('');
    }
  };

  const removeCC = (email: string) => {
    updateField('copies_to', formData.copies_to.filter(e => e !== email));
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      await onSend({
        transmittal_number: formData.transmittal_number,
        project_name: formData.project_name,
        project_number: formData.project_number,
        from_company: formData.from_company,
        from_name: formData.from_name,
        from_email: formData.from_email,
        to_company: formData.to_company,
        to_name: formData.to_name,
        to_email: formData.to_email,
        subject: formData.subject,
        reason: formData.reason,
        notes: formData.notes,
        items: formData.items,
        status: 'sent',
        sent_at: new Date().toISOString()
      });
      onClose();
    } catch (error) {
      console.error('Failed to send transmittal:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('transmittal-preview');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Transmittal - ${formData.transmittal_number}</title>
              <style>
                body { font-family: system-ui, sans-serif; padding: 20px; }
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
                th { background: #f5f5f5; }
                .header { display: flex; justify-content: space-between; border-bottom: 2px solid #333; padding-bottom: 16px; margin-bottom: 24px; }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Create Transmittal</h2>
              <p className="text-sm text-gray-400">{formData.items.length} drawing(s) selected</p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex items-center gap-4">
            <div className="flex rounded-lg bg-gray-800 p-1">
              <button
                onClick={() => setActiveTab('form')}
                className={`px-4 py-1.5 rounded text-sm ${
                  activeTab === 'form' ? 'bg-blue-600 text-white' : 'text-gray-400'
                }`}
              >
                <Edit2 className="w-4 h-4 inline mr-1" />
                Edit
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-4 py-1.5 rounded text-sm ${
                  activeTab === 'preview' ? 'bg-blue-600 text-white' : 'text-gray-400'
                }`}
              >
                <Eye className="w-4 h-4 inline mr-1" />
                Preview
              </button>
            </div>
            
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-gray-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'form' ? (
            <div className="p-6 space-y-6">
              {/* Transmittal Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Transmittal No.</label>
                  <input
                    type="text"
                    value={formData.transmittal_number}
                    onChange={(e) => updateField('transmittal_number', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => updateField('date', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Reason</label>
                  <select
                    value={formData.reason}
                    onChange={(e) => updateField('reason', e.target.value as any)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  >
                    {TRANSMITTAL_REASONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Project Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Project Name</label>
                  <input
                    type="text"
                    value={formData.project_name}
                    onChange={(e) => updateField('project_name', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Project Number</label>
                  <input
                    type="text"
                    value={formData.project_number}
                    onChange={(e) => updateField('project_number', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                </div>
              </div>

              {/* From/To */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                    <User className="w-4 h-4" /> From
                  </h4>
                  <input
                    type="text"
                    placeholder="Company"
                    value={formData.from_company}
                    onChange={(e) => updateField('from_company', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                  <input
                    type="text"
                    placeholder="Name"
                    value={formData.from_name}
                    onChange={(e) => updateField('from_name', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.from_email}
                    onChange={(e) => updateField('from_email', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                  <input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={formData.from_phone}
                    onChange={(e) => updateField('from_phone', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                </div>
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> To
                  </h4>
                  <input
                    type="text"
                    placeholder="Company"
                    value={formData.to_company}
                    onChange={(e) => updateField('to_company', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                  <input
                    type="text"
                    placeholder="Attention: Name"
                    value={formData.to_name}
                    onChange={(e) => updateField('to_name', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.to_email}
                    onChange={(e) => updateField('to_email', e.target.value)}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                  <textarea
                    placeholder="Address (optional)"
                    value={formData.to_address}
                    onChange={(e) => updateField('to_address', e.target.value)}
                    rows={2}
                    className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700 resize-none"
                  />
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => updateField('subject', e.target.value)}
                  className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                />
              </div>

              {/* Items */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Documents ({formData.items.length})</h4>
                <div className="border border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Drawing No.</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Title</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-400">Rev</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-400">Copies</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Remarks</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, idx) => (
                        <tr key={idx} className="border-t border-gray-800">
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.drawing_number}
                              onChange={(e) => updateItem(idx, 'drawing_number', e.target.value)}
                              className="w-full bg-gray-800 text-white text-sm rounded px-2 py-1 border border-gray-700 font-mono"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) => updateItem(idx, 'title', e.target.value)}
                              className="w-full bg-gray-800 text-white text-sm rounded px-2 py-1 border border-gray-700"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="text"
                              value={item.revision}
                              onChange={(e) => updateItem(idx, 'revision', e.target.value)}
                              className="w-16 bg-gray-800 text-white text-sm rounded px-2 py-1 border border-gray-700 text-center"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="number"
                              min="1"
                              value={item.copies}
                              onChange={(e) => updateItem(idx, 'copies', parseInt(e.target.value) || 1)}
                              className="w-16 bg-gray-800 text-white text-sm rounded px-2 py-1 border border-gray-700 text-center"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.remarks || ''}
                              onChange={(e) => updateItem(idx, 'remarks', e.target.value)}
                              placeholder="Optional remarks"
                              className="w-full bg-gray-800 text-white text-sm rounded px-2 py-1 border border-gray-700"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => removeItem(idx)}
                              className="p-1 text-gray-500 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  rows={3}
                  placeholder="Additional notes or instructions..."
                  className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700 resize-none"
                />
              </div>

              {/* CC */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Copy To (CC)</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="email"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCC())}
                    placeholder="Add email address..."
                    className="flex-1 bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-700"
                  />
                  <button
                    onClick={addCC}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {formData.copies_to.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.copies_to.map(email => (
                      <span key={email} className="flex items-center gap-1 px-2 py-1 bg-gray-800 text-gray-300 rounded text-sm">
                        {email}
                        <button onClick={() => removeCC(email)} className="hover:text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-6">
              <TransmittalPreview data={formData} companyLogo={companyLogo} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-800">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={isSending || !formData.to_email}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSending ? (
                <>Sending...</>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Transmittal
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
